/**
 * Yapılandırılmış e-fatura okuma — EN 16931 ailesi.
 *
 * TEMEL FİKİR: belge makine okunur veri taşıyorsa ONU oku, taşımıyorsa
 * çıkarıma düş. Bir faturayı pikselden ya da metin düzeninden çıkarmak her
 * zaman tahmin içerir; XML'den okumak içermez. Tarih belirsizliği yok, ondalık
 * ayracı belirsizliği yok, kaçan satır yok.
 *
 * Avrupa normu EN 16931 anlamsal modeli tanımlar, iki sözdizimi vardır:
 *
 *   UBL 2.1  → Peppol BIS 3.0, XRechnung, Türkiye UBL-TR   (`Invoice` kökü)
 *   CII      → Factur-X, ZUGFeRD                (`CrossIndustryInvoice` kökü)
 *
 * Factur-X/ZUGFeRD'in önemi: XML'i PDF'in İÇİNE gömer. Kullanıcı sıradan bir
 * PDF yüklediğini sanır; dosyanın içinde faturanın tam ve kesin hâli durur.
 * Bugün o eki görmeden sayfayı resme çevirip modele yolluyorduk.
 *
 * ÜLKEYE ÖZEL KOD YOKTUR. Türkiye'nin UBL-TR'si UBL 2.1'dir ve buradan
 * kendiliğinden okunur; ileride bir ülke ek alan isterse o alan buraya
 * eklenir, ayrı bir okuyucu yazılmaz.
 */

import { type XmlNode, parseXml, findFirst, findAll, text, child } from './xml.js';

export type EInvoiceSyntax = 'ubl' | 'cii';

export interface EInvoiceLine {
  /** Kalem açıklaması. */
  readonly description: string;
  /** Satır toplamı (vergi hariç), belgenin kendi para biriminde. */
  readonly amount: number;
  readonly quantity: number | null;
  readonly unitPrice: number | null;
  /** Yüzde olarak vergi oranı (20 = %20). Belirtilmemişse null. */
  readonly taxPercent: number | null;
}

export interface EInvoice {
  readonly syntax: EInvoiceSyntax;
  readonly invoiceId: string;
  /** ISO tarih (YYYY-MM-DD). Okunamazsa boş dize. */
  readonly issueDate: string;
  readonly currency: string;
  readonly sellerName: string;
  readonly buyerName: string;
  readonly lines: readonly EInvoiceLine[];
  /** Vergi hariç toplam. */
  readonly netTotal: number | null;
  readonly taxTotal: number | null;
  /** Ödenecek toplam (vergi dahil). */
  readonly grossTotal: number | null;
}

/**
 * XML sayısı oku.
 *
 * Yerel ayar ayrıştırıcısı KULLANILMAZ ve kullanılmamalı: XML'de sayılar
 * standarda göre daima nokta ondalıklı ve binlik ayraçsızdır. Buraya yerel
 * ayar sokmak, kesin olan bir veriye belirsizlik enjekte etmek olurdu.
 */
function num(raw: string): number | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** ISO tarihe indirge. UBL zaten YYYY-MM-DD verir; CII `102` biçimi YYYYAAGG. */
function isoDate(raw: string, format?: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{8}$/.test(s) && (!format || format === '102')) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return '';
}

/* ── UBL 2.1 ───────────────────────────────────────────────────────── */

function readUbl(root: XmlNode): EInvoice {
  const currency = text(root, 'DocumentCurrencyCode') || '';

  const partyName = (holder: XmlNode | null): string => {
    if (!holder) return '';
    const party = child(holder, 'Party');
    /* Kayıtlı ticari unvan varsa o tercih edilir; yoksa PartyName. */
    return (
      text(party, 'PartyLegalEntity', 'RegistrationName')
      || text(party, 'PartyName', 'Name')
      || ''
    );
  };

  const lines: EInvoiceLine[] = findAll(root, 'InvoiceLine').map((ln) => {
    const item = child(ln, 'Item');
    const priceAmt = num(text(ln, 'Price', 'PriceAmount'));
    const qty = num(child(ln, 'InvoicedQuantity')?.text ?? '');
    const taxPct = num(
      text(item, 'ClassifiedTaxCategory', 'Percent'),
    );
    return {
      description: text(item, 'Name') || text(item, 'Description') || '',
      amount: num(text(ln, 'LineExtensionAmount')) ?? 0,
      quantity: qty,
      unitPrice: priceAmt,
      taxPercent: taxPct,
    };
  });

  const totals = findFirst(root, 'LegalMonetaryTotal');

  return {
    syntax: 'ubl',
    invoiceId: text(root, 'ID'),
    issueDate: isoDate(text(root, 'IssueDate')),
    currency,
    sellerName: partyName(child(root, 'AccountingSupplierParty')),
    buyerName: partyName(child(root, 'AccountingCustomerParty')),
    lines,
    netTotal: num(text(totals, 'TaxExclusiveAmount')) ?? num(text(totals, 'LineExtensionAmount')),
    taxTotal: num(text(findFirst(root, 'TaxTotal'), 'TaxAmount')),
    grossTotal: num(text(totals, 'PayableAmount')) ?? num(text(totals, 'TaxInclusiveAmount')),
  };
}

/* ── CII (Factur-X / ZUGFeRD) ──────────────────────────────────────── */

function readCii(root: XmlNode): EInvoice {
  const doc = findFirst(root, 'ExchangedDocument');
  const settlement = findFirst(root, 'ApplicableHeaderTradeSettlement');
  const summary = findFirst(root, 'SpecifiedTradeSettlementHeaderMonetarySummation');

  const dateNode = doc ? child(doc, 'IssueDateTime') : null;
  const dateString = dateNode ? child(dateNode, 'DateTimeString') : null;

  const partyName = (n: XmlNode | null): string => (n ? text(n, 'Name') : '');
  const agreement = findFirst(root, 'ApplicableHeaderTradeAgreement');

  const lines: EInvoiceLine[] = findAll(root, 'IncludedSupplyChainTradeLineItem').map((ln) => {
    const product = child(ln, 'SpecifiedTradeProduct');
    const lineAgreement = child(ln, 'SpecifiedLineTradeAgreement');
    const delivery = child(ln, 'SpecifiedLineTradeDelivery');
    const lineSettlement = child(ln, 'SpecifiedLineTradeSettlement');
    const lineSummary = lineSettlement
      ? child(lineSettlement, 'SpecifiedTradeSettlementLineMonetarySummation')
      : null;
    const price = lineAgreement
      ? (child(lineAgreement, 'NetPriceProductTradePrice')
        ?? child(lineAgreement, 'GrossPriceProductTradePrice'))
      : null;

    return {
      description: text(product, 'Name') || text(product, 'Description') || '',
      amount: num(text(lineSummary, 'LineTotalAmount')) ?? 0,
      quantity: num(child(delivery, 'BilledQuantity')?.text ?? ''),
      unitPrice: num(text(price, 'ChargeAmount')),
      taxPercent: num(
        text(child(lineSettlement, 'ApplicableTradeTax'), 'RateApplicablePercent'),
      ),
    };
  });

  return {
    syntax: 'cii',
    invoiceId: text(doc, 'ID'),
    issueDate: isoDate(dateString?.text ?? '', dateString?.attrs.format),
    currency: text(settlement, 'InvoiceCurrencyCode') || '',
    sellerName: partyName(child(agreement, 'SellerTradeParty')),
    buyerName: partyName(child(agreement, 'BuyerTradeParty')),
    lines,
    netTotal: num(text(summary, 'TaxBasisTotalAmount'))
      ?? num(text(summary, 'LineTotalAmount')),
    taxTotal: num(text(summary, 'TaxTotalAmount')),
    grossTotal: num(text(summary, 'GrandTotalAmount'))
      ?? num(text(summary, 'DuePayableAmount')),
  };
}

/* ── Genel giriş ───────────────────────────────────────────────────── */

/**
 * Bu XML bir e-fatura mı, hangi sözdiziminde?
 *
 * Kök etiketin adına bakılır — ad alanı zaten sökülmüş durumda, dolayısıyla
 * hangi önek kullanılırsa kullanılsın tanınır.
 */
export function detectSyntax(root: XmlNode): EInvoiceSyntax | null {
  if (findFirst(root, 'CrossIndustryInvoice')) return 'cii';
  if (root.name === 'Invoice' || root.name === 'CreditNote') return 'ubl';
  if (findFirst(root, 'InvoiceLine') && findFirst(root, 'LegalMonetaryTotal')) return 'ubl';
  return null;
}

/**
 * XML metnini e-faturaya çevir. E-fatura değilse null — istisna atmaz.
 *
 * Çağıran için sözleşme: null dönerse normal çıkarım yoluna düş.
 */
export function parseEInvoice(xml: string): EInvoice | null {
  const root = parseXml(xml);
  if (!root || !root.name) return null;
  const syntax = detectSyntax(root);
  if (!syntax) return null;
  try {
    const invoice = syntax === 'cii' ? readCii(root) : readUbl(root);
    /* Ne kalem ne de toplam varsa bu tanınabilir bir fatura değildir;
       yarım okunmuş bir belgeyi kesin veri gibi sunmaktansa hiç sunma. */
    if (!invoice.lines.length && invoice.grossTotal === null) return null;
    return invoice;
  } catch {
    return null;
  }
}

/**
 * Bir dosya adı Factur-X/ZUGFeRD eki mi?
 *
 * PDF ekleri arasında bu adları arıyoruz. Liste standartların kendi
 * belirlediği adlardır; büyük/küçük harf farkı görmezden gelinir.
 */
export function isEInvoiceAttachment(name: string): boolean {
  const n = String(name ?? '').toLowerCase().trim();
  return (
    n === 'factur-x.xml'
    || n === 'zugferd-invoice.xml'
    || n === 'xrechnung.xml'
    || n === 'order-x.xml'
    || n === 'cii.xml'
  );
}

/** Bir defter satırı için ihtiyaç duyulan asgari şekil. */
export interface LedgerRow {
  date: string;
  note: string;
  amount: number;
  type: 'income' | 'expense';
  cur: string;
}

/**
 * E-faturayı defter satırlarına çevir.
 *
 * Satırlar KALEM KALEM üretilir, tek bir toplam satırı olarak değil: muhasebeci
 * kalemleri ayrı ayrı kategorilendirebilmeli. Kalem yoksa tek bir toplam
 * satırına düşülür — bilgi kaybetmemek için.
 *
 * `type` çağırandan gelir çünkü aynı fatura satan için gelir, alan için
 * giderdir; bunu belge değil bağlam belirler.
 */
export function toLedgerRows(
  invoice: EInvoice,
  type: 'income' | 'expense',
  fallbackCurrency = '',
): LedgerRow[] {
  const cur = invoice.currency || fallbackCurrency;
  const date = invoice.issueDate;
  const who = type === 'expense' ? invoice.sellerName : invoice.buyerName;

  if (!invoice.lines.length) {
    const total = invoice.grossTotal ?? invoice.netTotal;
    if (total === null) return [];
    return [{
      date,
      note: [who, invoice.invoiceId].filter(Boolean).join(' · ').slice(0, 100)
        || 'E-Fatura',
      amount: Math.abs(total),
      type,
      cur,
    }];
  }

  return invoice.lines
    .filter((l) => Number.isFinite(l.amount) && Math.abs(l.amount) > 0)
    .map((l) => ({
      date,
      note: [l.description, who].filter(Boolean).join(' · ').slice(0, 100)
        || invoice.invoiceId || 'E-Fatura',
      amount: Math.abs(l.amount),
      type,
      cur,
    }));
}
